import { RejectReason } from 'src/app/shared/models/RejectReason';

export type SetValuesFileAttachment = { name: string; base64: string };

const SET_VALUES_SKIP_HEADERS = new Set([
  'key',
  'x-background-request',
  'x-skip-loader',
]);

/** Quita Key del objeto legacy antes de armar FormData gateway (auth solo Bearer). */
export function omitLegacyKeyFromSetValuesHeaders(
  headers: Record<string, string>
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(headers)) {
    if (k.trim().toLowerCase() === 'key') {
      continue;
    }
    out[k] = v;
  }
  return out;
}

/**
 * Convierte cabecera legacy (MX_TKT_H, FLAG_PEDIDO_PEN, etc.) a campo FormData del gateway.
 * Contrato Swagger QAS: PascalCase (MxTktH, MxNumeroPedidoH, FlagPedidoPen, …).
 */
export function legacyHeaderNameToSetValuesFormField(name: string): string {
  const upper = name.trim().toUpperCase();
  if (upper === 'MX_NUMERO_PEDID_H') {
    return 'MxNumeroPedidoH';
  }
  if (upper === 'FLAG_PEDIDO_PEN') {
    return 'FlagPedidoPen';
  }
  if (upper === 'ES_GUIA_DIGITAL') {
    return 'EsGuiaDigital';
  }
  if (upper === 'FIN_FLUJO_H') {
    return 'FinFlujoH';
  }
  if (upper.startsWith('MX_')) {
    const parts = upper.slice(3).split('_').filter(Boolean);
    return (
      'Mx' +
      parts.map((p) => p.charAt(0) + p.slice(1).toLowerCase()).join('')
    );
  }
  if (upper.startsWith('M_')) {
    const parts = upper.slice(2).split('_').filter(Boolean);
    return (
      'M' +
      parts.map((p) => p.charAt(0) + p.slice(1).toLowerCase()).join('')
    );
  }
  return name.trim();
}

/** JSON para POST /viajes/set-values (sin archivos). */
export function legacySetValuesHeadersToGatewayJson(
  headers: Record<string, string>
): Record<string, string> {
  const body: Record<string, string> = {};
  for (const [k, v] of Object.entries(headers)) {
    if (SET_VALUES_SKIP_HEADERS.has(k.trim().toLowerCase())) {
      continue;
    }
    if (v === undefined || v === null) {
      continue;
    }
    body[legacyHeaderNameToSetValuesFormField(k)] = String(v);
  }
  return body;
}

function base64DataUrlToFile(base64: string, name: string): File {
  const [header, data] = base64.split(',');
  const mime = header.match(/:(.*?);/)?.[1] ?? 'image/jpeg';
  const byteString = atob(data);
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  return new File([ab], name, { type: mime });
}

/** FormData multipart para POST gateway set-values (QAS exige multipart/form-data). */
export async function buildSetValuesGatewayFormData(
  headers: Record<string, string>,
  files: File[] = [],
  queuedFiles: SetValuesFileAttachment[] = []
): Promise<FormData> {
  const fd = new FormData();
  const fields = legacySetValuesHeadersToGatewayJson(headers);
  for (const [k, v] of Object.entries(fields)) {
    fd.append(k, v);
  }

  const fileList =
    queuedFiles.length > 0
      ? queuedFiles.map((f) => base64DataUrlToFile(f.base64, f.name))
      : files;

  if (fileList.length > 0) {
    fd.append('etiqueta_archivo', fileList[0].name);
    for (const f of fileList) {
      fd.append('archivos', f, f.name);
    }
  }
  return fd;
}

/** FormData solo para rollback legacy .aspx (headers HTTP + archivos en body). */
export function legacySetValuesHeadersToFormData(
  headers: Record<string, string>,
  files: File[] = [],
  etiquetaArchivo?: string
): FormData {
  const fd = new FormData();
  const json = legacySetValuesHeadersToGatewayJson(headers);
  for (const [k, v] of Object.entries(json)) {
    fd.append(k, v);
  }
  if (etiquetaArchivo !== undefined && etiquetaArchivo !== null) {
    fd.append('etiqueta_archivo', String(etiquetaArchivo));
  } else if (files.length > 0) {
    fd.append('etiqueta_archivo', files[0].name);
  }
  for (const f of files) {
    fd.append(f.name, f, f.name);
  }
  return fd;
}



/** Motivos rechazo agrupados por negocio (POST /motivos/rechazo y legacy .aspx). */
export type RejectReasonsByNeg = Record<string, string[]>;

function parseRejectReasonsByNegPayload(raw: unknown): RejectReasonsByNeg {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return {};
  }
  const c = raw as Record<string, unknown>;
  const out: RejectReasonsByNeg = {};
  for (const neg of Object.keys(c)) {
    const inner = c[neg];
    if (!Array.isArray(inner)) {
      continue;
    }
    const descriptions = inner
      .filter((d) => d !== undefined && d !== null && String(d).trim() !== '')
      .map((d) => String(d));
    if (descriptions.length > 0) {
      out[neg] = descriptions;
    }
  }
  return out;
}

/**
 * POST /motivos/rechazo — `{ "3100": ["motivo", ...], ... }` (opcional envuelto en `datos`).
 * Misma forma que el GET legacy para rechazo-camion y observaciones.
 */
export function normalizeGatewayRejectReasonsGrouped(
  raw: unknown
): RejectReasonsByNeg {
  const direct = parseRejectReasonsByNegPayload(raw);
  if (Object.keys(direct).length > 0) {
    return direct;
  }
  if (!raw || typeof raw !== 'object') {
    return {};
  }
  const datos = (raw as Record<string, unknown>)['datos'];
  if (datos && typeof datos === 'object' && !Array.isArray(datos)) {
    return parseRejectReasonsByNegPayload(datos);
  }
  if (Array.isArray(datos) && datos.length > 0) {
    return parseRejectReasonsByNegPayload(datos[0]);
  }
  return {};
}

/**
 * Respuestas legacy con filas `{ neg, id, description }` o mapas id → descripción.
 * (`Record<neg, Record<id, descripción>>`).
 */
export function normalizeRejectReasonsGrouped(raw: unknown): Record<
  string,
  Record<string, string>
> {
  if (Array.isArray(raw)) {
    const byNeg: Record<string, Record<string, string>> = {};
    for (const row of raw as unknown[]) {
      if (!row || typeof row !== 'object') continue;
      const o = row as Record<string, unknown>;
      const neg = String(o['neg'] ?? o['NEG'] ?? '0');
      const id = String(o['id'] ?? o['ID'] ?? '');
      const desc = String(o['description'] ?? o['descripcion'] ?? '');
      if (!id) continue;
      if (!byNeg[neg]) byNeg[neg] = {};
      byNeg[neg][id] = desc;
    }
    if (Object.keys(byNeg).length > 0) {
      return byNeg;
    }
  }
  if (!raw || typeof raw !== 'object') {
    return {};
  }
  const r = raw as Record<string, unknown>;
  const datos = r['datos'];
  let candidate: unknown = r;
  if (Array.isArray(datos) && datos.length > 0) {
    candidate = datos[0];
  } else if (datos && typeof datos === 'object' && !Array.isArray(datos)) {
    candidate = datos;
  }

  if (!candidate || typeof candidate !== 'object') {
    return {};
  }

  const c = candidate as Record<string, unknown>;

  if (Array.isArray(c['motivos'])) {
    const byNeg: Record<string, Record<string, string>> = {};
    for (const row of c['motivos'] as unknown[]) {
      if (!row || typeof row !== 'object') continue;
      const o = row as Record<string, unknown>;
      const neg = String(o['neg'] ?? o['NEG'] ?? o['Neg'] ?? '0');
      const id = String(o['id'] ?? o['ID'] ?? o['codigo'] ?? '');
      const desc = String(o['description'] ?? o['DESCRIPCION'] ?? o['descripcion'] ?? '');
      if (!id) continue;
      if (!byNeg[neg]) byNeg[neg] = {};
      byNeg[neg][id] = desc;
    }
    return byNeg;
  }

  const out: Record<string, Record<string, string>> = {};
  for (const neg of Object.keys(c)) {
    const inner = c[neg];
    if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
      out[neg] = {};
      for (const [id, desc] of Object.entries(inner as Record<string, unknown>)) {
        out[neg][id] = String(desc);
      }
    }
  }
  if (Object.keys(out).length > 0) {
    return out;
  }

  return {};
}

/** Lista plana de `RejectReason` (p. ej. pedido-entregados) a partir de respuesta agrupada o plana. */
export function rejectReasonsToFlatList(data: unknown): RejectReason[] {
  if (Array.isArray(data)) {
    return (data as unknown[]).map((row) => {
      if (row instanceof RejectReason) return row;
      const o = row as Record<string, unknown>;
      return new RejectReason(
        Number(o['id']),
        String(o['description'] ?? o['descripcion'] ?? ''),
        String(o['neg'] ?? o['NEG'] ?? '')
      );
    });
  }
  const byNegLists = normalizeGatewayRejectReasonsGrouped(data);
  if (Object.keys(byNegLists).length > 0) {
    const acc: RejectReason[] = [];
    for (const neg of Object.keys(byNegLists)) {
      byNegLists[neg].forEach((description, idx) => {
        acc.push(new RejectReason(idx, description, neg));
      });
    }
    return acc;
  }
  const grouped = normalizeRejectReasonsGrouped(data);
  const acc: RejectReason[] = [];
  for (const neg of Object.keys(grouped)) {
    for (const [id, description] of Object.entries(grouped[neg])) {
      acc.push(new RejectReason(Number(id), description, neg));
    }
  }
  return acc;
}

/** Motivos retorno: legacy era array de un solo par clave/valor por ítem. */
export function normalizeMotivosRetornoToLegacyRows(
  raw: unknown
): Array<Record<string, string>> {
  if (Array.isArray(raw)) {
    const arr = raw as unknown[];
    if (arr.length && typeof arr[0] === 'object' && !Array.isArray(arr[0])) {
      const first = arr[0] as Record<string, unknown>;
      if ('id' in first || 'ID' in first) {
        return arr.map((row) => {
          const o = row as Record<string, unknown>;
          const id = String(o['id'] ?? o['ID'] ?? '');
          const desc = String(o['description'] ?? o['descripcion'] ?? '');
          return { [id]: desc };
        });
      }
    }
    return arr as Array<Record<string, string>>;
  }
  if (!raw || typeof raw !== 'object') {
    return [];
  }
  const r = raw as Record<string, unknown>;
  const datos = r['datos'];
  if (Array.isArray(datos)) {
    return normalizeMotivosRetornoToLegacyRows(datos);
  }
  return [];
}


